import { createSelector } from "reselect";
import { resolveAccessContext } from "@sdk/access/resolve-access-context";
import { parseTokenAccessFromJwt } from "@sdk/access/parse-jwt-access";
import type { iUserTokenAccess } from "@sdk/access/access.models";
import { unwindBy } from "utils/unwind-array";
import { iStore } from "../../store.model";
import { defaultQueryValue } from "../../utils/list-query";
import groupBy from "lodash/groupBy";

const selectQueryMap = (state: iStore) => state.users.queries;
const selectQueryMapAll = (state: iStore) => selectQueryMap(state)["all"];
const selectQueryAllList = (state: iStore) => selectQueryMap(state).all?.list;
export const selectUsersMap = (state: iStore) => state.users.byIds;

export const selectAllAPIUsers = createSelector(
  [selectQueryAllList, selectUsersMap],
  (list, map) =>
    list
      ?.map((listId) => map[listId])
      .filter((d) => d.metaData.isActive && d.metaData.isAPIUser) || []
);

export const selectAllUsers = createSelector(
  [selectQueryAllList, selectUsersMap],
  (list, map) =>
    list
      ?.map((listId) => map[listId])
      // Probably the user is not loaded yet
      .filter((user) => user)
      .filter((d) => d.metaData.isActive && !d.metaData.isAPIUser) || []
);

export const selectAllActiveAndInactiveUsers = createSelector(
  [selectQueryAllList, selectUsersMap],
  (list, map) =>
    list
      ?.map((listId) => map[listId])
      // Probably the user is not loaded yet
      .filter((user) => user)
      .filter((d) => !d.metaData.isAPIUser) || []
);

export const selectUsersByUserGroup = createSelector(
  [selectAllUsers],
  (userList) => groupBy(unwindBy(userList, "userGroups"), "userGroups")
);

export const selectAllActiveUsersQuery = createSelector(
  [selectQueryMapAll, selectUsersMap],
  (query, userMap) => {
    const populatedList = query?.list.map((listId) => userMap[listId]);
    return {
      ...defaultQueryValue,
      ...query,
      list:
        populatedList?.filter(
          (d) => d.metaData.isActive && !d.metaData.isAPIUser
        ) || [],
    };
  }
);

export const selectAllUsersQuery = createSelector(
  [selectQueryMapAll, selectUsersMap],
  (query, userMap) => {
    const populatedList = query?.list.map((listId) => userMap[listId]);
    return {
      ...defaultQueryValue,
      ...query,
      list: populatedList,
    };
  }
);
export const selectAccessToken = (state: iStore) => state.users.accessToken;

export const selectCurrentUser = (state: iStore) =>
  selectUser(state.users.currentUser!)(state);

export const selectCurrentUserEmail = (state: iStore) =>
  selectUser(state.users.currentUser!)(state)?.credentials?.email;

export const selectCurrentUserId = (state: iStore) => state.users.currentUser!;

export const selectCurrentUserAvailabilityStatus = (state: iStore) =>
  selectUser(state.users.currentUser!)(state)?.metaData?.availabilityStatus;

export const selectCurrentUserPresets = (state: iStore) =>
  (selectUser(state.users.currentUser!)(state).data.presets as string[]) || [];

export const selectUser = (userId: string) => (state: iStore) =>
  state.users.byIds[userId];

export const selectCurrentUserData = (state: iStore) =>
  selectUser(state.users.currentUser!)(state)?.data;

export const selectCurrentUserDisabledFromJoiningChats = (state: iStore) =>
  selectCurrentUser(state)?.metaData?.disableChatJoining;

function toUserTokenAccess(
  raw: ReturnType<typeof parseTokenAccessFromJwt>
): iUserTokenAccess | null {
  if (!raw) return null;

  if (Array.isArray(raw.capabilities)) {
    return {
      isOwner: Boolean(raw.isOwner),
      capabilities: raw.capabilities as iUserTokenAccess["capabilities"],
      brandIds: (raw.brandIds ?? []) as iUserTokenAccess["brandIds"],
    };
  }

  return {
    isOwner: Boolean(raw.isOwner),
    orgCaps: (raw.orgCaps ?? []) as iUserTokenAccess["orgCaps"],
    brands: Object.fromEntries(
      Object.entries(raw.brands ?? {}).map(([brandId, caps]) => [
        brandId,
        caps as NonNullable<iUserTokenAccess["orgCaps"]>,
      ])
    ),
  };
}

/** Prefer stored user.access; fall back to JWT v2/v1 token payload. */
export const selectAccessContext = createSelector(
  [selectCurrentUser, selectCurrentUserId, selectAccessToken],
  (user, userId, accessToken) => {
    if (!user || !userId || !user.organizationId) {
      return null;
    }

    const tokenAccess = accessToken
      ? toUserTokenAccess(parseTokenAccessFromJwt(accessToken))
      : null;

    return resolveAccessContext({
      userId,
      organizationId: user.organizationId,
      access: user.access,
      tokenAccess,
    });
  }
);
